import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCart, updateQuantity, removeFromCart } from "../utils/cart";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const CartPage = () => {
  const { t } = useTranslation();
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    setCartItems(getCart());
  }, []);

  const total = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const handleQtyChange = (id, change) => {
    const updated = cartItems.map((item) =>
      item.id === id
        ? { ...item, quantity: Math.max(1, item.quantity + change) }
        : item
    );

    setCartItems(updated);
    updateQuantity(id, updated.find((i) => i.id === id).quantity);
  };

  const handleRemove = (id) => {
    const updated = cartItems.filter((item) => item.id !== id);
    setCartItems(updated);
    removeFromCart(id);
  };

  if (cartItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-6">
        <h1 className="text-3xl font-bold text-fecos-text mb-4">{t("cart.emptyTitle")}</h1>
        <p className="text-fecos-gray mb-8 max-w-sm">
          {t("cart.emptyText")}
        </p>
        <Link
          to="/all-product"
          className="px-8 py-3 bg-fecos-text text-white rounded-full font-bold text-sm tracking-wide hover:bg-black transition-colors"
        >
          {t("cart.startShopping")}
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-fecos-bg min-h-screen py-16 px-6 lg:px-24">
      <div className="max-w-[1200px] mx-auto">
        <h1 className="text-4xl font-bold text-fecos-text mb-12 tracking-tight">{t("cart.title")}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-20">

          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-8">
            <AnimatePresence>
              {cartItems.map((item) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20, height: 0, marginBottom: 0 }}
                  className="flex items-center gap-6 p-6 bg-white rounded-3xl shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="w-24 h-24 bg-fecos-bg rounded-2xl flex items-center justify-center p-2 shrink-0">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-contain mix-blend-multiply"
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-bold text-fecos-text truncate">{item.name}</h2>
                    <p className="text-fecos-accent font-medium mt-1">{item.price} ₺</p>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center bg-fecos-bg rounded-lg p-1">
                      <button onClick={() => handleQtyChange(item.id, -1)} className="p-2 hover:bg-white rounded-md transition-colors"><Minus className="w-3 h-3 text-fecos-text" /></button>
                      <span className="w-8 text-center text-xs font-bold text-fecos-text">{item.quantity}</span>
                      <button onClick={() => handleQtyChange(item.id, +1)} className="p-2 hover:bg-white rounded-md transition-colors"><Plus className="w-3 h-3 text-fecos-text" /></button>
                    </div>
                    <button onClick={() => handleRemove(item.id)} className="p-2 text-gray-400 hover:text-red-500 transition-colors">
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>

                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white p-8 rounded-3xl shadow-lg sticky top-32">
              <h2 className="text-lg font-bold text-fecos-text mb-6">{t("cart.summary.title")}</h2>

              <div className="space-y-4 mb-8 text-sm">
                <div className="flex justify-between text-fecos-text/70">
                  <span>{t("cart.summary.subtotal")}</span>
                  <span className="font-medium text-fecos-text">{total} ₺</span>
                </div>
                <div className="flex justify-between text-fecos-text/70">
                  <span>{t("cart.summary.tax")}</span>
                  <span className="font-medium text-fecos-text">{t("cart.summary.included")}</span>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-6 flex justify-between items-center mb-8">
                <span className="text-base font-bold text-fecos-text">{t("cart.summary.total")}</span>
                <span className="text-2xl font-bold text-fecos-text">{total} ₺</span>
              </div>

              <Link
                to="/checkout"
                className="w-full py-4 bg-fecos-text text-white rounded-2xl font-bold text-sm tracking-wide flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                {t("cart.checkout")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
